import {ChipKind} from './aggregate';
import {GrantView, ImpersonationKind} from './grant-view';
import {
    mockClaudeRunStatsResponse,
    mockClaudeRunsResponse,
    mockGrantsResponse,
    mockRequestsResponse,
    mockTenantsResponse,
} from './mock-data';

/**
 * Backend base URL. The API Gateway is PRIVATE — only resolvable through the Twingate
 * connector in bastion-vpc — so this hostname is hardcoded. For local dev, override via
 * `VITE_API_BASE` in `.env.local`.
 */
const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.access-ops.ennabl.xyz';
const useMock = import.meta.env.VITE_MOCK === '1';

export interface GrantsResponse {
    grants: GrantView[];
    errors: {source: string; message: string}[];
    count: number;
    /** epoch-ms when `runSync()` last completed, or undefined before the first sync. */
    syncedAt?: number;
}

/**
 * One tenant from the full catalog (`GET /tenants`). `id` is the shard uid —
 * the form to submit in `tenantIds`. `accountId` is display/match only.
 */
export interface TenantCatalogEntry {
    id: string;
    name: string;
    accountId?: string;
}

export interface TenantsResponse {
    tenants: TenantCatalogEntry[];
    count: number;
}

export interface SyncResult {
    sync: {
        startedAt: number;
        finishedAt: number;
        sourceErrors: {source: string; message: string}[];
        counts: {written: number; deleted: number; total: number};
    };
    syncedAt: number;
}

/**
 * Which capability a history event belongs to. Impersonation is per application:
 * events logged before the split carry no `grantKind`, so the reader defaults
 * them to Decisions (every impersonation grant that existed then was Decisions).
 */
export type GrantHistoryKind = ChipKind;

export interface TempAccessRequest {
    /** Which source the event came from — drives chip color and filter pills. */
    kind: GrantHistoryKind;
    grantedAt: string;
    /** Populated for legacy DA events. Undefined for Impersonation rows. */
    username?: string;
    /** Populated for Impersonation rows from access-ops's own log group. */
    email?: string;
    /** Admin/requester who triggered an Impersonation grant. Undefined for DA. */
    grantedBy?: string;
    /** Shard id, or `'*'` for All-tenants Impersonation grants. */
    shardId: string;
    reason: string;
    policyTimestamp: string;
}

export interface RequestsResponse {
    requests: TempAccessRequest[];
    /**
     * DDB-backed AccessRequest rows in the same time window. Returned by
     * `/requests/history` (admin-only) so the UI can join CloudWatch grants
     * with the request-side view to surface status pills + `synced from
     * <source>` for log events that don't map back to a request.
     *
     * Optional in the type because the legacy mock path doesn't populate it;
     * the real backend always does.
     */
    accessRequests?: AccessRequest[];
    count: number;
    accessRequestCount?: number;
}

export interface RequestsFetchOpts {
    /** Lower bound (inclusive), unix ms. Defaults server-side to 7 days ago. */
    since?: number;
    /** Upper bound (inclusive), unix ms. Defaults server-side to now. */
    until?: number;
    /** Max requests returned. Defaults server-side to 1000, capped at 10_000. */
    limit?: number;
}

/**
 * Thrown when the browser can't reach the API at all — almost always because
 * the user isn't on Twingate. `fetch` rejects with a TypeError (`Failed to
 * fetch` in Chromium, `Load failed` in Safari) for DNS / TLS / network errors,
 * and there's no way to distinguish those from each other inside the page;
 * since this hostname only resolves through Twingate, we treat any TypeError
 * here as "Twingate is down" and surface a more useful message.
 */
export class TwingateUnreachableError extends Error {
    readonly cause?: unknown;
    constructor(cause?: unknown) {
        super('Twingate unreachable');
        this.name = 'TwingateUnreachableError';
        this.cause = cause;
    }
}

/** Thrown when the API returns 401 — the Google id token has expired. */
export class SessionExpiredError extends Error {
    constructor() {
        super('Session expired');
        this.name = 'SessionExpiredError';
    }
}

/**
 * App registers a handler here so that any fetch (route loader OR side fetch
 * from a drawer / sub-component) can ask App to clear the session on 401
 * without prop-drilling a callback through every caller.
 */
let sessionExpiredHandler: (() => void) | null = null;

export function setSessionExpiredHandler(fn: (() => void) | null) {
    sessionExpiredHandler = fn;
}

/**
 * Hard cap on how long we wait for /grants. The Lambda itself caps each adapter
 * at 15s; in practice cold-start with all sources fresh comes back in ~10s, warm
 * runs in ~6s. 15s here is enough headroom for a legitimate cold start while
 * keeping the Twingate-down case (where the hostname doesn't resolve through
 * Twingate's split DNS and the TCP connect hangs on stale OS state) from making
 * the user stare at a spinner for a full minute.
 */
const FETCH_TIMEOUT_MS = 15_000;
// Logs Insights queries for a full month complete in ~4s; pad heavily for very
// wide windows or cold paths and bound at 60s.
const REQUESTS_FETCH_TIMEOUT_MS = 60_000;

export async function fetchGrants(token: string): Promise<GrantsResponse> {
    if (useMock) {
        return mockGrantsResponse();
    }
    return await fetchJson<GrantsResponse>('/grants', token, FETCH_TIMEOUT_MS);
}

/**
 * Full tenant catalog for the Grant/Request access picker — every tenant, not
 * just those with an existing grant. Independent of `/grants` so a tenant with
 * zero grants is still selectable.
 */
export async function fetchTenants(token: string): Promise<TenantsResponse> {
    if (useMock) {
        return mockTenantsResponse();
    }
    return await fetchJson<TenantsResponse>('/tenants', token, FETCH_TIMEOUT_MS);
}

/**
 * Admin-only — triggers a full sync of the DDB snapshot from the live sources
 * (IAM Bastion DataAccess + AccessOps impersonation). Returns when the sync finishes.
 * Wired to the UI Reload button so admins can refresh the cache on demand.
 */
export async function syncGrants(token: string): Promise<SyncResult> {
    if (useMock) {
        return {
            sync: {
                startedAt: Date.now(),
                finishedAt: Date.now(),
                sourceErrors: [],
                counts: {written: 0, deleted: 0, total: 0},
            },
            syncedAt: Date.now(),
        };
    }
    return await fetchJsonWithBody<SyncResult>('/grants/sync', 'POST', token, {}, FETCH_TIMEOUT_MS);
}

export async function fetchRequests(token: string, opts: RequestsFetchOpts = {}): Promise<RequestsResponse> {
    if (useMock) {
        return mockRequestsResponse(opts);
    }
    const params = new URLSearchParams();
    if (opts.since !== undefined) {
        params.set('since', String(opts.since));
    }
    if (opts.until !== undefined) {
        params.set('until', String(opts.until));
    }
    if (opts.limit !== undefined) {
        params.set('limit', String(opts.limit));
    }
    const qs = params.toString();
    // Phase 3: `/requests` is now the AccessRequest CRUD endpoint. The legacy
    // CloudWatch-Insights view of `check-user-access` temp-grants moved to
    // `/requests/history`.
    return await fetchJson<RequestsResponse>(
        `/requests/history${qs ? `?${qs}` : ''}`,
        token,
        REQUESTS_FETCH_TIMEOUT_MS
    );
}

// -- Claude runs (admin audit log of self-hosted Claude Code runner tasks) ---

export type ClaudeRunStatus = 'denied' | 'spawning' | 'spawned' | 'failed';

/**
 * One task launch attempt of a Claude Code self-hosted environment, as stored in
 * `access-ops-claude-runs` (written by the orchestrators in INFRA). A session that idles, is
 * released and later resumed produces one row per task. The row records the launch decision
 * only; how the task then ran is in ECS and CloudWatch, which `logGroup`/`logStream` link to.
 */
export interface ClaudeRun {
    id: string;
    status: ClaudeRunStatus;
    /** claude.ai environment display name, e.g. `ennabl-infra (aws cloud)`. */
    environment: string;
    scope: string;
    hostingAccount: string;
    /** Session creator (lowercase email). */
    email: string;
    /** Bastion IAM user the email resolved to; absent for denied rows. */
    user?: string;
    sessionId?: string;
    /** claude.ai transcript of the session. */
    sessionUrl?: string;
    repo?: string;
    repoUrl?: string;
    revision?: string;
    /** epoch-ms when the orchestrator handled the spawn request. */
    requestedAt: number;
    /** Why the launch was refused — set on `denied` rows. */
    denyReason?: string;
    /** Why RunTask did not produce a task — set on `failed` rows. */
    failureReason?: string;
    taskArn?: string;
    taskId?: string;
    taskFamily?: string;
    size?: string;
    logGroup?: string;
    logStream?: string;
}

export interface ClaudeRunsResponse {
    runs: ClaudeRun[];
    count: number;
    since: number;
    until: number;
}

/** Admin-only. Newest first within `[since, until]` on `requestedAt`; server default is 7 days. */
export async function fetchClaudeRuns(token: string, opts: RequestsFetchOpts = {}): Promise<ClaudeRunsResponse> {
    if (useMock) {
        return mockClaudeRunsResponse(opts);
    }
    const params = new URLSearchParams();
    if (opts.since !== undefined) {
        params.set('since', String(opts.since));
    }
    if (opts.until !== undefined) {
        params.set('until', String(opts.until));
    }
    if (opts.limit !== undefined) {
        params.set('limit', String(opts.limit));
    }
    const qs = params.toString();
    return await fetchJson<ClaudeRunsResponse>(`/claude-runs${qs ? `?${qs}` : ''}`, token, FETCH_TIMEOUT_MS);
}

/** One model's token totals for a session, as the session reported them. */
export interface SessionUsage {
    model: string;
    speed: string;
    messages: number;
    input: number;
    output: number;
    cacheRead: number;
    cacheWrite5m: number;
    cacheWrite1h: number;
}

/**
 * What a session was asked and what it used (`GET /claude-runs/stats`, keyed by run id). Reported by
 * the session itself after every turn (a Stop hook in the runner image), so informational only; costs
 * are estimates (`functions/shared/claude-run-cost.ts`).
 */
export interface RunStats {
    /** First prompt of the session, up to 120 characters. */
    title?: string;
    prompts?: number;
    /** First and last transcript activity, ISO. */
    activeFrom?: string;
    activeTo?: string;
    /** Task runtime (first to last log event of its stream), ms. */
    runtimeMs?: number;
    usage: SessionUsage[];
    /** API-equivalent price of the reported tokens, USD. */
    claudeUsd?: number;
    unpricedModels: string[];
    /** On-demand compute estimate, USD. */
    awsUsd?: number;
}

export interface ClaudeRunStatsResponse {
    stats: Record<string, RunStats>;
    count: number;
}

/** Admin-only. Same window as `fetchClaudeRuns`; slower (Logs Insights in INFRA), so loaded separately. */
export async function fetchClaudeRunStats(
    token: string,
    opts: RequestsFetchOpts = {}
): Promise<ClaudeRunStatsResponse> {
    if (useMock) {
        return mockClaudeRunStatsResponse(opts);
    }
    const params = new URLSearchParams();
    if (opts.since !== undefined) {
        params.set('since', String(opts.since));
    }
    if (opts.until !== undefined) {
        params.set('until', String(opts.until));
    }
    if (opts.limit !== undefined) {
        params.set('limit', String(opts.limit));
    }
    const qs = params.toString();
    return await fetchJson<ClaudeRunStatsResponse>(
        `/claude-runs/stats${qs ? `?${qs}` : ''}`,
        token,
        REQUESTS_FETCH_TIMEOUT_MS
    );
}

// -- Phase 3: auth context, AccessRequest CRUD, user admin -------------------

export type Role = 'admin' | 'user';
export type UserGroup = 'trusted' | 'needs-approval';
export type RequestStatus = 'pending' | 'auto-approved' | 'approved' | 'denied';

export interface Me {
    email: string;
    role: Role;
    group: UserGroup;
}

export interface GrantBlock {
    /** Array of tenant ids, or the single ['*'] meaning all tenants. */
    tenantIds: string[];
}

export interface AccessRequest {
    id: string;
    requesterEmail: string;
    createdAt: number;
    dataAccess?: GrantBlock;
    /** DECISIONS impersonation — legacy field name, see the Lambda's requests-repo. */
    impersonation?: GrantBlock;
    /** ENNABL AI impersonation. */
    impAi?: GrantBlock;
    reason: string;
    status: RequestStatus;
    decidedBy?: string;
    decidedAt?: number;
    decidedReason?: string;
}

export interface UserProfile {
    email: string;
    role: Role;
    group: UserGroup;
    updatedAt?: number;
    updatedBy?: string;
}

export interface CreateRequestInput {
    dataAccess?: {tenantIds: string[] | '*'};
    /** DECISIONS impersonation. */
    impersonation?: {tenantIds: string[] | '*'};
    /** ENNABL AI impersonation. `tenantIds` are SUBJECTS — email / domain / `'*'`.
     *  `expiryDays` absent = permanent (which the server treats as elevated and
     *  routes to admin approval even for a trusted requester). */
    impAi?: {tenantIds: string[] | '*'; expiryDays?: number};
    reason: string;
    /** Admin-only: email of the user to grant access *to*. Ignored otherwise. */
    onBehalfOf?: string;
}

export async function fetchMe(token: string): Promise<Me> {
    if (useMock) {
        // Mock mode: default to admin so the whole UI is exercisable. The
        // `mockRole=user` localStorage override lets the screenshot harness
        // (and any future dev) snap user-side screens without a code edit.
        const role: Role =
            typeof window !== 'undefined' && window.localStorage?.getItem('mockRole') === 'user' ? 'user' : 'admin';
        return {email: 'mock@ennabl.com', role, group: 'trusted'};
    }
    return await fetchJson<Me>('/me', token, FETCH_TIMEOUT_MS);
}

export async function fetchMyRequests(token: string): Promise<AccessRequest[]> {
    if (useMock) {
        return mockMyRequests();
    }
    const r = await fetchJson<{requests: AccessRequest[]}>('/requests?scope=mine', token, FETCH_TIMEOUT_MS);
    return r.requests;
}

export async function fetchPendingRequests(token: string): Promise<AccessRequest[]> {
    if (useMock) {
        return mockPendingRequests();
    }
    const r = await fetchJson<{requests: AccessRequest[]}>('/requests?scope=pending', token, FETCH_TIMEOUT_MS);
    return r.requests;
}

/**
 * Canned "My requests" rows for `VITE_MOCK=1`. Picked to demonstrate each
 * status pill on the same list (pending / auto-approved / approved / denied)
 * so screenshots and visual debugging cover the variations without scripting.
 */
function mockMyRequests(): AccessRequest[] {
    const now = Date.now();
    const m = (offset: number) => now - offset;
    return [
        {
            id: 'mock-mine-1',
            requesterEmail: 'mock@ennabl.com',
            createdAt: m(2 * 60_000),
            dataAccess: {tenantIds: ['*']},
            reason: 'Investigating customer ticket ENN-1042 — needs full data view',
            status: 'pending',
        },
        {
            id: 'mock-mine-2',
            requesterEmail: 'mock@ennabl.com',
            createdAt: m(35 * 60_000),
            dataAccess: {tenantIds: ['b6e5bf', '013611968594']},
            reason: 'Repro reported broker-roster mismatch on Alera and Arlington Roe',
            status: 'auto-approved',
            decidedBy: 'mock@ennabl.com',
            decidedAt: m(35 * 60_000),
            decidedReason: 'auto-approved (trusted)',
        },
        {
            id: 'mock-mine-3',
            requesterEmail: 'mock@ennabl.com',
            createdAt: m(4 * 60 * 60_000),
            impersonation: {tenantIds: ['b6e5bf']},
            reason: 'Customer call at 16:00 — need to see what Alera admins see when claims drawer opens',
            status: 'approved',
            decidedBy: 'andriy@ennabl.com',
            decidedAt: m(3 * 60 * 60_000),
            decidedReason: 'approved for the call',
        },
        {
            id: 'mock-mine-4',
            requesterEmail: 'mock@ennabl.com',
            createdAt: m(2 * 24 * 60 * 60_000),
            dataAccess: {tenantIds: ['*']},
            reason: 'short reason without enough justification',
            status: 'denied',
            decidedBy: 'andriy@ennabl.com',
            decidedAt: m(2 * 24 * 60 * 60_000 - 30 * 60_000),
            decidedReason: 'Please open a ticket and reference it in the reason',
        },
    ];
}

/**
 * Canned admin "Pending approvals" queue for `VITE_MOCK=1`. Covers the
 * three submission shapes admins typically see: all-tenants DA, per-tenant
 * DA from a needs-approval user, and an impersonation request.
 */
function mockPendingRequests(): AccessRequest[] {
    const now = Date.now();
    const m = (offset: number) => now - offset;
    return [
        {
            id: 'mock-pending-1',
            requesterEmail: 'alex@ennabl.com',
            createdAt: m(3 * 60_000),
            dataAccess: {tenantIds: ['*']},
            reason: 'Need all-tenant view to investigate cross-tenant aggregation bug in production',
            status: 'pending',
        },
        {
            id: 'mock-pending-2',
            requesterEmail: 'pavel@ennabl.com',
            createdAt: m(18 * 60_000),
            dataAccess: {tenantIds: ['b6e5bf']},
            reason: 'Customer asked us to verify a policy id mismatch — Alera Group only',
            status: 'pending',
        },
        {
            id: 'mock-pending-3',
            requesterEmail: 'pavle@ennabl.com',
            createdAt: m(55 * 60_000),
            impersonation: {tenantIds: ['013611968594']},
            reason: 'Onboarding session with Arlington Roe — need to walk through their admin UI',
            status: 'pending',
        },
    ];
}

export async function createRequest(token: string, input: CreateRequestInput): Promise<AccessRequest> {
    if (useMock) {
        const adminMode = typeof input.onBehalfOf === 'string' && input.onBehalfOf.trim().length > 0;
        return {
            id: `mock-${Date.now()}`,
            requesterEmail: adminMode ? input.onBehalfOf!.trim() : 'mock@ennabl.com',
            createdAt: Date.now(),
            dataAccess: input.dataAccess ? {tenantIds: toArray(input.dataAccess.tenantIds)} : undefined,
            impersonation: input.impersonation ? {tenantIds: toArray(input.impersonation.tenantIds)} : undefined,
            reason: input.reason,
            status: adminMode ? 'approved' : 'pending',
            decidedBy: adminMode ? 'mock-admin@ennabl.com' : undefined,
            decidedAt: adminMode ? Date.now() : undefined,
            decidedReason: adminMode ? 'admin direct grant' : undefined,
        };
    }
    const r = await fetchJsonWithBody<{request: AccessRequest}>('/requests', 'POST', token, input, FETCH_TIMEOUT_MS);
    return r.request;
}

export async function decideAccessRequest(
    token: string,
    id: string,
    decision: 'approve' | 'deny',
    decidedReason?: string
): Promise<AccessRequest> {
    if (useMock) {
        return {
            id,
            requesterEmail: 'mock@ennabl.com',
            createdAt: Date.now() - 60_000,
            reason: 'mock',
            status: decision === 'approve' ? 'approved' : 'denied',
            decidedBy: 'mock-admin@ennabl.com',
            decidedAt: Date.now(),
            decidedReason,
        };
    }
    const r = await fetchJsonWithBody<{request: AccessRequest}>(
        `/requests/${id}`,
        'PATCH',
        token,
        {decision, decidedReason},
        FETCH_TIMEOUT_MS
    );
    return r.request;
}

export async function fetchUsers(token: string): Promise<UserProfile[]> {
    if (useMock) {
        // Synthesize a recipient roster from the canned grants so the Grant
        // access dialog has something to autocomplete against in mock mode.
        const now = Date.now();
        const emails = new Set<string>();
        for (const g of mockGrantsResponse().grants) {
            if (g.email) {
                emails.add(g.email);
            }
        }
        return Array.from(emails)
            .sort()
            .map(email => ({
                email,
                role: 'user' as const,
                group: 'needs-approval' as const,
                updatedAt: now,
                updatedBy: 'mock@ennabl.com',
            }));
    }
    const r = await fetchJson<{users: UserProfile[]}>('/users', token, FETCH_TIMEOUT_MS);
    return r.users;
}

export interface RevokeResult {
    grantId: string;
    kind: 'data-access-all-tenants' | 'iam-temp' | ImpersonationKind;
    ok: boolean;
    error?: string;
    /** Impersonation: did a DDB row actually get deleted? false = already gone. */
    removed?: boolean;
}

export async function revokeGrant(token: string, grantId: string): Promise<RevokeResult> {
    if (useMock) {
        return {grantId, kind: 'data-access-all-tenants', ok: true};
    }
    const r = await fetchJsonWithBody<{revoke: RevokeResult}>(
        '/grants/revoke',
        'POST',
        token,
        {grantId},
        FETCH_TIMEOUT_MS
    );
    return r.revoke;
}

export async function patchUser(token: string, email: string, role: Role, group: UserGroup): Promise<UserProfile> {
    if (useMock) {
        return {email, role, group, updatedAt: Date.now(), updatedBy: 'mock-admin@ennabl.com'};
    }
    const r = await fetchJsonWithBody<{user: UserProfile}>(
        `/users/${encodeURIComponent(email)}`,
        'PATCH',
        token,
        {role, group},
        FETCH_TIMEOUT_MS
    );
    return r.user;
}

function toArray(t: string[] | '*'): string[] {
    return t === '*' ? ['*'] : t;
}

async function fetchJsonWithBody<T>(
    path: string,
    method: 'POST' | 'PATCH' | 'DELETE',
    token: string,
    body: unknown,
    timeoutMs: number
): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
        response = await fetch(`${apiBase}${path}`, {
            method,
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        });
    } catch (err) {
        throw new TwingateUnreachableError(err);
    } finally {
        clearTimeout(timer);
    }
    if (response.status === 401) {
        sessionExpiredHandler?.();
        throw new SessionExpiredError();
    }
    if (!response.ok) {
        const text = await response.text();
        throw new Error(`${method} ${path} failed: ${response.status} ${text}`);
    }
    return (await response.json()) as T;
}

async function fetchJson<T>(path: string, token: string, timeoutMs: number): Promise<T> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
        response = await fetch(`${apiBase}${path}`, {
            headers: {Authorization: `Bearer ${token}`},
            signal: controller.signal,
        });
    } catch (err) {
        // Both a network failure (TypeError: "Failed to fetch" / "Load failed")
        // and the abort timeout are surfaced the same way to the user — we have
        // no way to reach the API, please connect Twingate.
        throw new TwingateUnreachableError(err);
    } finally {
        clearTimeout(timer);
    }
    if (response.status === 401) {
        // Token's expired. Let App clear the session so the user goes back to
        // Google sign-in (One Tap usually re-auths silently if the Google
        // session is still alive).
        sessionExpiredHandler?.();
        throw new SessionExpiredError();
    }
    if (!response.ok) {
        const text = await response.text();
        throw new Error(`GET ${path} failed: ${response.status} ${text}`);
    }
    return (await response.json()) as T;
}
